import { Component, computed, effect, inject, OnInit, signal } from "@angular/core";
import { TranslocoModule } from "@jsverse/transloco";
import { JourneyBreadcrumb } from "../../common/journey-breadcrumb/journey-breadcrumb";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { CardResultSmall } from "src/app/shared/components/card-result-small/card-result-small";
import { Loader } from "src/app/shared/components/loader/loader";
import { Button } from "src/app/shared/components/utils/button/button";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { JourneySearchStore } from "src/app/features/store/journey-search-store";
import { Router } from "@angular/router";
import { ProgressionBar } from "../../common/progression-bar/progression-bar";
import {
  TileInterface,
  TileStatusEnum,
} from "src/app/core/interfaces/tile-interface";
import { Tile } from "src/app/shared/components/tile/tile";
import { Toggle } from "src/app/shared/components/toggle/toggle";
import { UserStore } from "src/app/features/store/user-store";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteTileErrorModal } from "src/app/shared/components/favorite-tile-error-modal/favorite-tile-error-modal";

@Component({
  selector: "app-offers-page",
  imports: [
    TranslocoModule,
    JourneyBreadcrumb,
    Icon,
    CardResultSmall,
    Loader,
    Button,
    Modal,
    FavoriteTileErrorModal,
    ProgressionBar,
    Tile,
    Toggle,
  ],
  templateUrl: "./offers-page.html",
})
export class OffersPage implements OnInit {
  store = inject(JourneySearchStore);
  userStore = inject(UserStore);
  userRole = computed(() => this.userStore.getUserPermission());

  modeDraft = signal<boolean>(false);

  tilesApi = inject(TilesApiService);
  tilesOfferDiscovery = signal<TileInterface[] | undefined>(undefined);
  tilesOfferDiscoveryToShow = computed(() => {
    const availableTiles = this.tilesOfferDiscovery() ?? [];
    const sourceTiles = this.modeDraft()
      ? availableTiles
      : availableTiles.filter((tile) => tile.status === TileStatusEnum.Approved);
    return sourceTiles.map((tile, index) => ({
      ...tile,
      size: this.tilesApi.giveSizeToTiles(index),
    }));
  });

  router = inject(Router);

  jobLoading = computed(() => this.store.jobLoading());
  jobs = computed(() => this.store.jobs());
  extraJobsLoading = signal<boolean>(false);

  isRequestErrorModalOpen = computed(() =>
    this.store.isRequestErrorModalOpen()
  );

  tilesLoading = computed(() => this.store.tilesLoading());

  private readonly languageService = inject(LanguageService);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.reloadProjectTiles();
    });
  }

  ngOnInit(): void {
    this.store.loadJobs();
  }

  private async reloadProjectTiles(): Promise<void> {
    await this.store.loadTilesByTheme();
    this.tilesOfferDiscovery.set(this.store.tilesOffer());
  }

  async loadMoreJobs(): Promise<void> {
    this.store.jobsPagination().start = this.store.jobsPagination().start + 1;
    try {
      this.extraJobsLoading.set(true);
      await this.store.loadMoreJobs();
    } finally {
      this.extraJobsLoading.set(false);
    }
  }

  redirectToJourneyResearch(): void {
    this.router.navigate(["/journey/search"]);
  }

  closeRequestErrorModalAndRedirectToJobResearch(): void {
    this.store.setIsRequestErrorModalOpen(false);
    this.redirectToJourneyResearch();
  }

  closeRequestErrorModal(): void {
    this.store.setIsRequestErrorModalOpen(false);
  }

  clickOnTile(tile: TileInterface) {
    const link = tile?.ctaLink;
    if (link) {
      window.open(link, "_blank");
    }
  }

  toggleDraftMode(): void {
    this.modeDraft.set(!this.modeDraft());
  }
}
